import { z } from "zod";

/**
 * What the Chats address carries, so a search, a filter or a sort survives a reload, the back
 * button and a link. A value this build does not know falls back to its default instead of an
 * error page, and a default is left out of the address. `new` (with `record` and `prompt`) is what
 * an older link asked for when a new chat was a panel beside the list; the route sends it on to
 * the new chat's own page (`/chats/new`).
 */
export const chatsSearchSchema = z.object({
  q: z.string().optional().catch(undefined),
  tab: z.enum(["yours", "needs-you", "draft"]).optional().catch(undefined),
  by: z.enum(["me", "tom"]).optional().catch(undefined),
  kind: z.enum(["message", "file", "step"]).optional().catch(undefined),
  /** Archived chats are left out of the list unless this is on. */
  archived: z.boolean().optional().catch(undefined),
  new: z.boolean().optional().catch(undefined),
  record: z.boolean().optional().catch(undefined),
  prompt: z.string().max(500).optional().catch(undefined),
});

export type ChatsSearch = z.infer<typeof chatsSearchSchema>;

/** How the list was looked at: what the search, the filters and the sort left in the address. */
export type ListView = Omit<ChatsSearch, "new" | "record" | "prompt">;

/**
 * What an open chat's address carries: which panel sits beside it, so a reload and a link land where
 * you were. The Context rail, the canvas and a file's preview share one slot, so at most one of
 * them is open; the canvas names what it shows (`process:<id>`), the step selected in it, and
 * whether the review is open in the panel; a preview names its file (`file:<id>`), and a
 * recording's package view its recording (`recording:<id>`).
 */
export const openChatSearchSchema = z.object({
  panel: z.enum(["context"]).optional().catch(undefined),
  open: z
    .string()
    .regex(/^(process:[a-z0-9_-]+|file:[a-z0-9_-]+|recording:[a-z0-9_-]+)$/i)
    .optional()
    .catch(undefined),
  /** A step's id, or `proposal:<id>` for a step Abacor proposed that is not in the draft yet. */
  step: z.string().optional().catch(undefined),
  review: z.boolean().optional().catch(undefined),
});

export type OpenChatSearch = z.infer<typeof openChatSearchSchema>;

/** The id of the process the canvas shows, from `open`. */
export const openProcessId = (open: string | undefined): string | null =>
  open?.startsWith("process:") ? open.slice("process:".length) : null;

/** The id of the file whose preview is open, from `open`. */
export const openFileId = (open: string | undefined): string | null =>
  open?.startsWith("file:") ? open.slice("file:".length) : null;

/** The id of the recording whose package view is open, from `open`. */
export const openRecordingId = (open: string | undefined): string | null =>
  open?.startsWith("recording:") ? open.slice("recording:".length) : null;
